import { createContext, useContext, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

type RowTarget = { trigger: () => HTMLElement | null; row: () => HTMLElement | null };
export type HistoryFocusLease = {
  version: number;
  rowId: string;
  kind: "menu" | "dialog";
  neighbors: string[];
};

const eligible = (node: HTMLElement | null): node is HTMLElement => Boolean(
  node?.isConnected && !node.matches(":disabled, [aria-disabled='true']") &&
  !node.closest("[hidden], [inert]") && node.ownerDocument.defaultView?.getComputedStyle(node).visibility !== "hidden",
);

class HistoryFocusOwner {
  private version = 0;
  private active = false;
  private rows = new Map<string, RowTarget>();
  private latest: HistoryFocusLease | null = null;
  private content = new WeakMap<HistoryFocusLease, HTMLElement>();
  private released = new WeakSet<HistoryFocusLease>();
  private fallback: () => HTMLElement | null = () => null;
  private scopeFallback = false;

  activate(fallback: () => HTMLElement | null = () => null) {
    this.active = true;
    this.fallback = fallback;
    if (this.scopeFallback && document.activeElement === document.body) this.focus(fallback());
    this.scopeFallback = false;
  }

  deactivate() {
    const lease = this.latest;
    this.scopeFallback = Boolean(lease && (this.owns(lease, document.activeElement) ||
      (!this.released.has(lease) && document.activeElement === document.body)));
    this.active = false;
    this.version++;
    this.latest = null;
  }

  register(id: string, target: RowTarget) {
    this.rows.set(id, target);
    return () => {
      const lease = this.latest;
      const ownedFocus = lease?.rowId === id && this.owns(lease, document.activeElement);
      if (this.rows.get(id) === target) this.rows.delete(id);
      if (lease && ownedFocus) {
        // Run after React's removal commit, not while the old row is still
        // connected. Every deferred restoration revalidates its ownership.
        queueMicrotask(() => this.restore(lease, true));
      }
    };
  }

  claim(rowId: string, kind: HistoryFocusLease["kind"]) {
    const ids = [...this.rows.keys()];
    const index = ids.indexOf(rowId);
    const lease: HistoryFocusLease = {
      version: ++this.version, rowId, kind,
      neighbors: [...ids.slice(index + 1), ...ids.slice(0, index).reverse()],
    };
    this.latest = lease;
    return lease;
  }

  private owns(lease: HistoryFocusLease, node: Element | null) {
    return Boolean(node && (this.rows.get(lease.rowId)?.row()?.contains(node) || this.content.get(lease)?.contains(node)));
  }

  private focus(node: HTMLElement | null) {
    if (!eligible(node)) return false;
    node.focus();
    return document.activeElement === node;
  }

  isCurrent(lease: HistoryFocusLease | null) {
    return Boolean(lease && this.active && lease.version === this.version);
  }

  attach(lease: HistoryFocusLease | null, content: EventTarget | null) {
    if (!lease || !this.isCurrent(lease) || !(content instanceof HTMLElement)) return false;
    this.content.set(lease, content);
    return true;
  }

  restore(lease: HistoryFocusLease | null, afterRemoval = false) {
    if (!lease || !this.isCurrent(lease)) return;
    if (this.released.has(lease) && !afterRemoval) return;
    const focused = document.activeElement;
    if (afterRemoval && focused !== document.body && focused?.isConnected) return;
    // Respect a user/new overlay which has deliberately taken focus elsewhere.
    if (focused !== document.body && focused?.isConnected && !this.owns(lease, focused)) return;
    this.released.add(lease);
    if (this.focus(this.rows.get(lease.rowId)?.trigger() ?? null)) return;
    for (const id of [...lease.neighbors, ...this.rows.keys()]) {
      if (id !== lease.rowId && this.focus(this.rows.get(id)?.trigger() ?? null)) return;
    }
    this.focus(this.fallback());
  }
}

const HistoryFocusContext = createContext<HistoryFocusOwner | null>(null);

export function HistoryFocusBoundary({ scopeKey, children }: { scopeKey: string; children: ReactNode }) {
  const [owner] = useState(() => new HistoryFocusOwner());
  const fallback = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  useLayoutEffect(() => {
    owner.activate(() => fallback.current);
    return () => owner.deactivate();
  }, [owner, scopeKey]);
  return (
    <HistoryFocusContext.Provider value={owner}>
      <div ref={fallback} tabIndex={-1} role="region" aria-label={t("conversation.conversations")} className="focus-visible:outline-2 focus-visible:outline-ring">
        {children}
      </div>
    </HistoryFocusContext.Provider>
  );
}

export function useHistoryRowFocus(id: string) {
  const context = useContext(HistoryFocusContext);
  const [local] = useState(() => new HistoryFocusOwner());
  const owner = context ?? local;
  const trigger = useRef<HTMLButtonElement>(null);
  const row = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (context) return;
    owner.activate();
    return () => owner.deactivate();
  }, [context, owner]);
  useLayoutEffect(() => owner.register(id, { trigger: () => trigger.current, row: () => row.current }), [id, owner]);
  return { owner, trigger, row };
}
